import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyWallet } from '../api/fan-api';
import { getStoredFanClaims } from '../auth/fan-auth';
import { subscribeWalletChanged } from '../lib/wallet-bus';

/**
 * Always-visible platform-points counter in the top header — the
 * "coin counter" piece of the network-game HUD pattern (BottomNav
 * being the dock, this being the resource stat). Tappable shortcut
 * to /wallet.
 *
 * Self-fetches on mount, refreshes every 60s, and on window focus
 * (so balance reflects a topup or chat-spend that happened in
 * another tab). Silent on error — a HUD that crashes the header on
 * a flaky network is worse than a HUD that just shows "··".
 */
function CoinIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 6.75v10.5" />
      <path d="M14.25 9.75c0-1.243-1.007-2.25-2.25-2.25S9.75 8.507 9.75 9.75c0 1.107.798 2.027 1.849 2.213l.802.142c1.051.186 1.849 1.106 1.849 2.213 0 1.243-1.007 2.25-2.25 2.25s-2.25-1.007-2.25-2.25" />
    </svg>
  );
}

export function WalletPill() {
  const signedIn = Boolean(getStoredFanClaims()?.sub);
  const [balance, setBalance] = useState<number | null>(null);
  // 'gain' / 'spend' flash classes are toggled briefly when the
  // balance changes so the HUD reacts visibly (matches "coin counter"
  // feedback from arcade-style UIs).
  const [flash, setFlash] = useState<'none' | 'gain' | 'spend'>('none');
  const prevBalanceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    const fetchOnce = async () => {
      try {
        const w = await getMyWallet();
        if (alive) setBalance(w.platform.balance);
      } catch {
        // Silent — HUD must never crash the header. Stale value (or
        // "··" if we never had one) is better than an error toast.
      }
    };
    void fetchOnce();
    const interval = window.setInterval(fetchOnce, 60_000);
    const onFocus = () => { void fetchOnce(); };
    window.addEventListener('focus', onFocus);
    // Push-driven refresh — any in-app action that mutates the wallet
    // (activity claim, skin acquire, topup return) calls
    // `notifyWalletChanged()` from `lib/wallet-bus.ts` and we re-fetch
    // immediately instead of waiting for the next 60s tick.
    const unsubscribe = subscribeWalletChanged(() => { void fetchOnce(); });
    return () => {
      alive = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      unsubscribe();
    };
  }, [signedIn]);

  // React to balance change — flash gain (green) on increase, spend
  // (rose) on decrease. First load (null → value) doesn't flash.
  useEffect(() => {
    const prev = prevBalanceRef.current;
    if (balance != null && prev != null && balance !== prev) {
      setFlash(balance > prev ? 'gain' : 'spend');
      const t = window.setTimeout(() => setFlash('none'), 900);
      prevBalanceRef.current = balance;
      return () => window.clearTimeout(t);
    }
    prevBalanceRef.current = balance;
  }, [balance]);

  if (!signedIn) return null;

  const display = balance == null ? '··' : balance.toLocaleString();
  const flashClass =
    flash === 'gain' ? 'nx-wallet-flash-gain'
    : flash === 'spend' ? 'nx-wallet-flash-spend'
    : '';

  return (
    <Link
      to="/wallet"
      aria-label={balance == null ? '前往錢包' : `平台點數 ${balance}`}
      className={`inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/40 px-2.5 py-1 text-xs font-semibold text-amber-200 hover:bg-amber-500/25 active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-amber-400 transition shadow-[0_4px_12px_-6px_rgba(245,158,11,0.5)] ${flashClass}`}
    >
      <CoinIcon className="h-3.5 w-3.5" />
      <span className="tabular-nums leading-none">{display}</span>
    </Link>
  );
}
